import React, { useEffect, useState } from 'react';
import useAolSaveHandler from '../../../app/useAolSaveHandler';
import { useAolStore } from '../../../context/AolContext';
import { NoonReportBridgeSectionFactory } from '../../../factory/ValidatedNoonReportFactory';
import { NoonReportSectionEnum, NoonReportTypeEnum } from '../../../types/ViewModel/AolEnums';
import { ValidationItem } from '../../../types/ViewModel/ValidationItemType';
import { NoonReportSectionData } from '../../../types/ViewModel/NoonReportSectionData';
import { createSectionData, getNumberValue, hasInvalidFields, hasMissingValues } from '../../../utils/aolFieldsHelper';
import { ErrorText } from '../../shared/error-text/ErrorText.style';
import OdfjellInputComponent, { InputChangeData } from '../../common/odfjell-input-component/OdfjellInputComponent';
import { roundTo1Decimal, roundTo2Decimals } from '../../../utils/numberHelper';
import { WarningText } from '../../shared/warning-text/WarningText.style';
import useGetAolData from '../../../api/aol/useGetAolData';
import { useVesselStore } from '../../../context/VesselContext';
import { useAuthStore } from '../../../context/AuthContext';
import { formatDateTime, DateTimeFormat } from 'odfjell-date-time-utils/dateTimeFormatter';
import { ValidatedNoonReport } from 'src/types/ViewModel/ValidatedNoonReport';
import useReadOnlyUtils from 'src/utils/useReadOnlyUtils';

type VM = {
    forwardDraft?: number;
    aftDraft?: number;
    observedDistance?: number;
    observedSpeed?: number;
    navtorDistance?: number | null;
    isNavtorDistanceValid?: boolean;
    loggedDistance?: number;
    loggedSpeed?: number;
    ecaZoneHours?: number;
    sectionData?: NoonReportSectionData;
    notValidFieldsArray?: string[];
    hasInvalidFields?: boolean;
    isInitialized?: boolean;
};

const BridgeSectionComponent: React.FC = () => {
    const [bridgeVM, setBridgeVM] = useState<VM>({});
    const [isNavtorDistanceValid, setIsNavtorDistanceValid] = useState<boolean>(true);
    const [reportHours, setReportHours] = useState<number>(0);
    const [hasEmptyValues, setHasEmptyValues] = useState<boolean>(false);
    const [hasMissingDistance, setHasMissingDistance] = useState<boolean>(false);
    const [hasOptimalTrimRangeSoftError, setHasOptimalTrimRangeSoftError] = useState<boolean>(false);
    const { saveAolEvent, saveSection } = useAolSaveHandler();
    const { selectedAolDataItem, navtorDistance, setNavtorDistance } = useAolStore();
    const { getDistanceBetween } = useGetAolData();
    const { selectedVessel } = useVesselStore();
    const { loggedInUser } = useAuthStore();
    const { checkIsAolReadOnly } = useReadOnlyUtils();

    useEffect(() => {
        return () => {
            setNavtorDistance(null);
        };
    }, []);

    useEffect(() => {
        if (!selectedAolDataItem || !loggedInUser) return;

        if (!bridgeVM.isInitialized) {
            initVM(null, null);
            getNavtorDistance();
        }
    }, [selectedAolDataItem, selectedAolDataItem?.aolDataItemProperties, loggedInUser]);

    useEffect(() => {
        updateSpeedByDistance('observed');
        updateSpeedByDistance('logged');
    }, [reportHours]);

    useEffect(() => {
        if (
            saveAolEvent &&
            saveAolEvent.section === NoonReportSectionEnum.Bridge &&
            selectedAolDataItem &&
            saveAolEvent.aolDataItem.id === selectedAolDataItem.id
        ) {
            if (saveAolEvent.type === 'save_section') save();
            if (saveAolEvent.type === 'save_section_done') initVM(null, null);
        }
    }, [saveAolEvent]);

    const isReadOnly = checkIsAolReadOnly(bridgeVM?.sectionData);

    const getNavtorDistance = async (): Promise<void> => {
        setNavtorDistance(null);
        setIsNavtorDistanceValid(null);

        // Validate dates before making the API call
        if (!selectedAolDataItem.reportFromTimeUtc || !selectedAolDataItem.reportToTimeUtc) {
            console.warn('Cannot fetch Navtor distance: invalid report dates', {
                reportFromTimeUtc: selectedAolDataItem.reportFromTimeUtc,
                reportToTimeUtc: selectedAolDataItem.reportToTimeUtc,
            });
            initVM(null, null);
            return;
        }

        // Validate that dates are valid Date objects
        const fromDate = selectedAolDataItem.reportFromTimeUtc;
        const toDate = selectedAolDataItem.reportToTimeUtc;

        if (!(fromDate instanceof Date) || isNaN(fromDate.getTime()) || !(toDate instanceof Date) || isNaN(toDate.getTime())) {
            console.warn('Cannot fetch Navtor distance: dates are not valid Date objects', {
                reportFromTimeUtc: fromDate,
                reportToTimeUtc: toDate,
            });
            initVM(null, null);
            return;
        }

        const _navtorDistance = await getDistanceBetween(
            selectedVessel.imoNumber,
            formatDateTime(fromDate, DateTimeFormat.queryFormats.dateTime, 'utc'),
            formatDateTime(toDate, DateTimeFormat.queryFormats.dateTime, 'utc')
        );

        if (_navtorDistance && !bridgeVM.isInitialized) {
            const _isNavtorDistanceValid =
                selectedAolDataItem.reportHours > 0 &&
                _navtorDistance > 0 &&
                _navtorDistance / selectedAolDataItem.reportHours <= 25 &&
                _navtorDistance / selectedAolDataItem.reportHours > 0;

            setNavtorDistance(_navtorDistance);
            setIsNavtorDistanceValid(_isNavtorDistanceValid);
            initVM(_navtorDistance, _isNavtorDistanceValid);
        } else {
            initVM(null, null);
        }
    };

    const initVM = (navtorDist: number | null, isNavtorDistValid: boolean | null) => {
        if (!selectedAolDataItem) return;

        const _reportHours = roundTo2Decimals(
            (selectedAolDataItem.aolDataItemProperties.shiftingAndMooringPortActivityHours ?? 0) > 0
                ? selectedAolDataItem.aolDataItemProperties.shiftingAndMooringPortActivityHours
                : selectedAolDataItem.reportHours
        );

        setReportHours(_reportHours);

        const noonReport = selectedAolDataItem.noonReport;

        let _observedDistance = getNumberValue(noonReport?.bridgeSection?.observedDistance, 2, true);
        let _observedSpeed = getNumberValue(noonReport?.bridgeSection?.observedSpeed, 2, true);

        _observedDistance = updateNavtorDistance(_observedDistance, navtorDist, noonReport);
        _observedSpeed = updateNavtorSpeed(_observedSpeed, _observedDistance, navtorDist, noonReport, _reportHours);

        const vm: VM = {
            forwardDraft: getNumberValue(noonReport?.bridgeSection?.forwardDraft, 2),
            aftDraft: getNumberValue(noonReport?.bridgeSection?.aftDraft, 2),
            observedDistance: _observedDistance,
            observedSpeed: _observedSpeed,
            navtorDistance: navtorDist,
            isNavtorDistanceValid: isNavtorDistValid,
            loggedDistance: getNumberValue(noonReport?.bridgeSection?.loggedDistance, 2, true),
            loggedSpeed: getNumberValue(noonReport?.bridgeSection?.loggedSpeed, 2, true),
            ecaZoneHours: getNumberValue(noonReport?.bridgeSection?.ecaZoneHours, 1, true),
            sectionData: noonReport?.bridgeSection?.sectionData ?? createSectionData(NoonReportSectionEnum.Bridge),
            notValidFieldsArray: [],
            hasInvalidFields: false,
            isInitialized: true,
        };
        updateHasMissingDistance(vm);
        updateHasOptimalTrimRangeSoftError(vm);
        setHasEmptyValues(false);
        setBridgeVM({ ...vm });
    };

    const updateNavtorDistance = (observedDistance: number, navtorDist: number, noonReport: ValidatedNoonReport) => {
        if (
            navtorDist &&
            navtorDist > 0 &&
            navtorDist / selectedAolDataItem.reportHours <= 25 &&
            (noonReport?.bridgeSection?.observedDistance === null || noonReport?.bridgeSection?.observedDistance === undefined)
        ) {
            observedDistance = getNumberValue(navtorDist, 2, true);
        }

        return observedDistance;
    };

    const updateNavtorSpeed = (
        observedSpeed: number,
        observedDistance: number,
        navtorDist: number,
        noonReport: ValidatedNoonReport,
        reportHours: number
    ) => {
        if (
            navtorDist &&
            navtorDist > 0 &&
            (noonReport?.bridgeSection?.observedDistance === null || noonReport?.bridgeSection?.observedDistance === undefined)
        ) {
            observedDistance = getNumberValue(navtorDist, 2, true);

            if (reportHours && observedDistance !== undefined) {
                observedSpeed = observedDistance >= 0 ? roundTo2Decimals(observedDistance / reportHours) : undefined;
            }
        }

        return observedSpeed;
    };

    const updateHasMissingDistance = (vm: VM) => {
        if (!selectedAolDataItem) {
            return;
        }
        if (
            (selectedAolDataItem.aolDataItemProperties.shiftingAndMooringPortActivityHours ?? 0) > 0 &&
            (vm.loggedDistance === 0 || vm.observedDistance === 0)
        ) {
            setHasMissingDistance(true);
        } else {
            setHasMissingDistance(false);
        }
    };
    const updateHasOptimalTrimRangeSoftError = (vm: VM) => {
        if (!selectedAolDataItem) {
            return;
        }
        if (vm.aftDraft && vm.forwardDraft && Math.abs(vm.aftDraft - vm.forwardDraft) > 0.25) {
            setHasOptimalTrimRangeSoftError(true);
        } else if (hasOptimalTrimRangeSoftError) {
            setHasOptimalTrimRangeSoftError(false);
        }
    };

    const customValidations: ValidationItem[] = [
        {
            validationFunction: () => bridgeVM.aftDraft && bridgeVM.forwardDraft && Math.abs((bridgeVM.aftDraft - bridgeVM.forwardDraft) / 2) > 5,
            errorMessage: 'The absolute trim value is above 5m. Please check that the entered value is correct.',
        },
    ];

    const updateSpeedByDistance = (type: 'logged' | 'observed') => {
        const distance = bridgeVM[`${type}Distance`];
        const speedName = `${type}Speed`;

        if (reportHours && distance !== undefined) {
            bridgeVM[speedName] = distance >= 0 ? roundTo2Decimals(distance / reportHours) : undefined;
            setBridgeVM({ ...bridgeVM });
        }
    };

    const onInputChange = (changeObject: InputChangeData) => {
        const name = changeObject.event.target.name;
        const value = changeObject.event.target.value;
        bridgeVM[name] = value ? +value : undefined;

        if (name === 'observedDistance') updateSpeedByDistance('observed');
        if (name === 'loggedDistance') updateSpeedByDistance('logged');

        updateHasMissingDistance(bridgeVM);
        updateHasOptimalTrimRangeSoftError(bridgeVM);
        hasInvalidFields(bridgeVM, name, changeObject.isValid);

        setBridgeVM({ ...bridgeVM });
        if (hasEmptyValues) setHasEmptyValues(false);
    };

    const save = () => {
        const ignoreList: string[] = [];
        ignoreList.push('navtorDistance');
        ignoreList.push('isNavtorDistanceValid');

        if (selectedAolDataItem?.reportType !== NoonReportTypeEnum.Sea) {
            ignoreList.push('forwardDraft');
            ignoreList.push('aftDraft');
            ignoreList.push('ecaZoneHours');
        }

        if (selectedAolDataItem?.aolVersion > 2) {
            ignoreList.push('ecaZoneHours');
        }

        // Ensure bridgeVM has the current navtorDistance from context
        bridgeVM.navtorDistance = navtorDistance;
        bridgeVM.isNavtorDistanceValid = isNavtorDistanceValid;

        if (hasMissingValues(bridgeVM, ignoreList)) {
            setHasEmptyValues(true);
        } else {
            const data = new NoonReportBridgeSectionFactory().createBM(bridgeVM);
            if (hasEmptyValues) setHasEmptyValues(false);

            if (!bridgeVM.hasInvalidFields) {
                saveSection(data);
            }
        }
    };

    return (
        <>
            {selectedAolDataItem && selectedAolDataItem.reportType === NoonReportTypeEnum.Sea && (
                <>
                    <OdfjellInputComponent
                        isReadOnly={isReadOnly}
                        type={'number'}
                        description={'Forward draft'}
                        min={2}
                        max={15}
                        customValidations={customValidations}
                        value={bridgeVM.forwardDraft}
                        name={'forwardDraft'}
                        onChange={onInputChange}
                        unit={'m'}
                        leaveBlank={true}
                    />
                    <OdfjellInputComponent
                        type={'number'}
                        isReadOnly={isReadOnly}
                        description={'Aft draft'}
                        min={2}
                        max={15}
                        customValidations={customValidations}
                        value={bridgeVM.aftDraft}
                        name={'aftDraft'}
                        onChange={onInputChange}
                        unit={'m'}
                        leaveBlank={true}
                    />
                    {selectedAolDataItem.aolVersion <= 2 && (
                        <OdfjellInputComponent
                            isReadOnly={true}
                            type={'number'}
                            description={'Hours in ECA zone at sea'}
                            value={bridgeVM.ecaZoneHours}
                            onChange={onInputChange}
                            unit={'h'}
                        />
                    )}
                </>
            )}
            <OdfjellInputComponent
                isReadOnly={true}
                type={'number'}
                description={'Observed speed (SOG)'}
                value={bridgeVM.observedSpeed}
                min={0}
                max={25}
                onChange={onInputChange}
                unit={'knot'}
                tooltipId={'observedSpeed'}
            />
            <OdfjellInputComponent
                type={'number'}
                isReadOnly={isReadOnly}
                description={'Observed distance'}
                min={0}
                max={roundTo1Decimal(reportHours * 25)}
                value={bridgeVM.observedDistance}
                name={'observedDistance'}
                onChange={onInputChange}
                unit={'NM'}
            />
            <OdfjellInputComponent
                isReadOnly={true}
                type={'number'}
                description={'Logged speed (STW)'}
                value={bridgeVM.loggedSpeed}
                min={0}
                max={25}
                onChange={onInputChange}
                unit={'knot'}
                tooltipId={'loggedSpeed'}
            />
            <OdfjellInputComponent
                type={'number'}
                isReadOnly={isReadOnly}
                description={'Logged distance'}
                min={0}
                max={roundTo1Decimal(reportHours * 25)}
                value={bridgeVM.loggedDistance}
                name={'loggedDistance'}
                onChange={onInputChange}
                unit={'NM'}
            />
            {!!navtorDistance && (
                <OdfjellInputComponent
                    isReadOnly={true}
                    type={'number'}
                    description={'Navtor distance'}
                    value={navtorDistance}
                    name={'navtorDistance'}
                    unit={'NM'}
                />
            )}
            {hasMissingDistance && <WarningText>Missing distance during river or port maneuvering.</WarningText>}
            {hasOptimalTrimRangeSoftError && (
                <WarningText>
                    Odfjell Tankers best practice suggest a optimal trim is between 0,25m forward and 0,25m aft. Please evaluate if that can be
                    achived on the current voyage.
                </WarningText>
            )}
            {isNavtorDistanceValid === false && <WarningText>Navtor distance is not valid. Ensure correct observed distances.</WarningText>}
            {hasEmptyValues && <ErrorText>You must complete the section before saving.</ErrorText>}
        </>
    );
};

export default BridgeSectionComponent;
